import styles from "./ChartCell.module.scss";
import { FC, useMemo } from "react";
import { useAtomValue } from "jotai";
import { positiveState, negativeState, themeState } from "@/lib/store";
import { classNames } from "@/lib/utils";
import TimeSeriesChart, { type TimeSeries } from "@/components/ortex-charts/TimeSeriesChart";

interface PropsTypes {
    width?: number;
    height?: number;
    backgroundColor?: string;
    data: [number, number][] | { [key: string]: [number, number][] };
    isMultiTime?: boolean;
    datetime: boolean;
    direction?: string;
    type?: string;
    customTooltip?: { formatter: () => string };
    seriesOptions?: Array<Partial<TimeSeries> & { color?: string }>;
    obfuscated?: string;
}

/**
 * A chart in a table cell: a single price or return line, or the
 * Returns / Risk / Reward triple where the risk and reward areas are
 * stacked to 100% and the returns line reads on its own scale.
 */
const ChartCell: FC<PropsTypes> = ({ data, isMultiTime, datetime, direction, type, seriesOptions, obfuscated }) => {
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const theme = useAtomValue(themeState);

    // Without dates the x values are day counts ("after N days"); a day per unit puts them on the timeline.
    const toTime = (points: [number, number][] | undefined): [number, number][] => (points ?? []).map(([x, y]) => [datetime ? x : x * 86_400_000, y]);
    const series = useMemo<TimeSeries[]>(() => {
        if (!isMultiTime) {
            return [{ id: "value", title: datetime ? "Price" : "Return", type: (type as TimeSeries["type"]) || "line", data: toTime(data as [number, number][]), color: theme === "dark" ? "#FFF" : "#000", lineWidth: 1, precision: 2, legend: false }];
        }
        const entries = Object.entries(data as { [key: string]: [number, number][] })
            .reverse()
            .map(([name, points]) => [name, toTime(points)] as [string, [number, number][]]);
        // Percent stacking for the area series: each value as a share of the sum at that time.
        const areas = entries.filter(([name]) => !(name === "Returns" || type === "line"));
        const totals = new Map<number, number>();
        for (const [, points] of areas) for (const [t, v] of points) totals.set(t, (totals.get(t) ?? 0) + (v ?? 0));
        const stacked = new Map<number, number>();
        return entries.map(([name, points], index) => {
            const isArea = !(name === "Returns" || type === "line");
            const color = seriesOptions?.[index]?.color ? seriesOptions[index].color : name === "Returns" ? "#FFF" : name === "Risk" ? negative : positive;
            let pts: [number, number][] = points;
            if (isArea) {
                pts = pts.map(([t, v]) => {
                    const total = totals.get(t) || 1;
                    const share = ((v ?? 0) / total) * 100;
                    const base = stacked.get(t) ?? 0;
                    stacked.set(t, base + share);
                    return [t, base + share];
                });
            }
            return {
                id: name,
                title: name,
                type: isArea ? "area" : "line",
                data: pts,
                color,
                axis: name !== "Returns" ? "left" : "right",
                lineWidth: 1,
                precision: isArea ? 0 : 2,
                format: isArea ? "percent" : "price",
                fillOpacity: 0.5,
                ...seriesOptions?.[index],
            } as TimeSeries;
        });
    }, [data, isMultiTime, datetime, type, seriesOptions, theme, positive, negative]);

    const axes = useMemo(
        () => [
            { id: "left", visible: false, invert: direction === "Sell" },
            { id: "right", visible: false },
        ],
        [direction]
    );

    return (
        <div className={classNames(styles.ChartCell, obfuscated)} style={{ height: "100%" }}>
            <TimeSeriesChart
                series={series}
                axes={axes}
                timeAxis={false}
                grid={false}
                legend={false}
                interactive={false}
                tooltip={!obfuscated}
                crosshair={!obfuscated}
                tooltipTitle={datetime ? undefined : (t) => `After ${Math.round(t / 86_400_000)} days`}
                resolution="1D"
            />
        </div>
    );
};

export default ChartCell;
